// src/components/character/CharacterSettingsModal.tsx
// キャラクター設定編集モーダル

import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Character } from '../../types/character';
import { updateCharacter } from '../../services/characterService';
import { getTagCategory } from '../../constants/characterTags';

// 文字数制限の定義（全項目20文字）
const LIMITS = {
  text: 20,  // 全テキストフィールド共通
  array: { maxItems: 10, maxLength: 20 },  // 配列フィールド共通
};

interface CharacterSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  character: Character;
  onSaved: (updatedCharacter: Partial<Character>) => void;
}

const CharacterSettingsModal: React.FC<CharacterSettingsModalProps> = ({
  isOpen,
  onClose,
  character,
  onSaved,
}) => {
  const { t } = useTranslation('character');

  // tagsのジャンルからspeciesのデフォルト値を取得
  const getDefaultSpecies = (char: Character) => {
    if (char.species) return char.species;
    // tagsからジャンルタグを探す（人間、動物、ケモノ等）
    const genreTag = char.tags?.find(tag => getTagCategory(tag) === 'genre');
    return genreTag || '';
  };

  // Form state
  const [species, setSpecies] = useState(getDefaultSpecies(character));
  const [gender, setGender] = useState(character.gender || '');
  const [age, setAge] = useState(character.age || '');
  const [birthday, setBirthday] = useState(character.birthday || '');
  const [height, setHeight] = useState(character.height || '');
  const [personality, setPersonality] = useState<string[]>(character.personality || []);
  const [likes, setLikes] = useState<string[]>(character.likes || []);
  const [dislikes, setDislikes] = useState<string[]>(character.dislikes || []);

  // Input state for adding new items
  const [newPersonality, setNewPersonality] = useState('');
  const [newLike, setNewLike] = useState('');
  const [newDislike, setNewDislike] = useState('');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSpecies(getDefaultSpecies(character));
      setGender(character.gender || '');
      setAge(character.age || '');
      setBirthday(character.birthday || '');
      setHeight(character.height || '');
      setPersonality(character.personality || []);
      setLikes(character.likes || []);
      setDislikes(character.dislikes || []);
      setNewPersonality('');
      setNewLike('');
      setNewDislike('');
      setError(null);
    }
  }, [isOpen, character]);

  // Add item to array
  const addItem = useCallback((
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    inputSetter: React.Dispatch<React.SetStateAction<string>>,
    maxItems?: number
  ) => {
    const trimmed = value.trim();
    if (trimmed) {
      setter(prev => {
        if (maxItems && prev.length >= maxItems) return prev;
        return [...prev, trimmed];
      });
      inputSetter('');
    }
  }, []);

  // Remove item from array
  const removeItem = useCallback((
    index: number,
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setter(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const updates: Partial<Character> = {
        species: species.trim() || undefined,
        gender: gender.trim() || undefined,
        age: age.trim() || undefined,
        birthday: birthday.trim() || undefined,
        height: height.trim() || undefined,
        personality: personality.length > 0 ? personality : undefined,
        likes: likes.length > 0 ? likes : undefined,
        dislikes: dislikes.length > 0 ? dislikes : undefined,
      };

      await updateCharacter(character.id, updates);
      onSaved(updates);
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('settings.saveFailed', '保存に失敗しました');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle key press for adding items
  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    inputSetter: React.Dispatch<React.SetStateAction<string>>,
    maxItems?: number
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem(value, setter, inputSetter, maxItems);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg max-h-[90vh] bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {t('settings.editTitle', 'キャラクター設定')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Species */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('settings.species', '種族')}
              </label>
              <span className={`text-xs ${species.length > LIMITS.text ? 'text-red-500' : 'text-gray-400'}`}>
                {species.length}/{LIMITS.text}
              </span>
            </div>
            <input
              type="text"
              value={species}
              onChange={(e) => setSpecies(e.target.value)}
              maxLength={LIMITS.text}
              placeholder={t('settings.speciesPlaceholder', '例: 人間、エルフ、獣人...')}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Gender */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('settings.gender', '性別')}
              </label>
              <span className={`text-xs ${gender.length > LIMITS.text ? 'text-red-500' : 'text-gray-400'}`}>
                {gender.length}/{LIMITS.text}
              </span>
            </div>
            <input
              type="text"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              maxLength={LIMITS.text}
              placeholder={t('settings.genderPlaceholder', '例: 女性、男性、無性別...')}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Age */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('settings.age', '年齢')}
              </label>
              <span className={`text-xs ${age.length > LIMITS.text ? 'text-red-500' : 'text-gray-400'}`}>
                {age.length}/{LIMITS.text}
              </span>
            </div>
            <input
              type="text"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              maxLength={LIMITS.text}
              placeholder={t('settings.agePlaceholder', '例: 18歳、不明、永遠の17歳...')}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Birthday */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('settings.birthday', '誕生日')}
              </label>
              <span className={`text-xs ${birthday.length > LIMITS.text ? 'text-red-500' : 'text-gray-400'}`}>
                {birthday.length}/{LIMITS.text}
              </span>
            </div>
            <input
              type="text"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              maxLength={LIMITS.text}
              placeholder={t('settings.birthdayPlaceholder', '例: 3月15日、春の満月の日...')}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Height */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('settings.height', '身長')}
              </label>
              <span className={`text-xs ${height.length > LIMITS.text ? 'text-red-500' : 'text-gray-400'}`}>
                {height.length}/{LIMITS.text}
              </span>
            </div>
            <input
              type="text"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              maxLength={LIMITS.text}
              placeholder={t('settings.heightPlaceholder', '例: 160cm、小柄、とても大きい...')}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Personality */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('settings.personality', '性格')}
              </label>
              <span className={`text-xs ${personality.length >= LIMITS.array.maxItems ? 'text-red-500' : 'text-gray-400'}`}>
                {personality.length}/{LIMITS.array.maxItems}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              {personality.map((trait, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-sm rounded-full"
                >
                  {trait}
                  <button
                    type="button"
                    onClick={() => removeItem(index, setPersonality)}
                    className="hover:text-gray-900 dark:hover:text-gray-100"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newPersonality}
                onChange={(e) => setNewPersonality(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, newPersonality, setPersonality, setNewPersonality, LIMITS.array.maxItems)}
                maxLength={LIMITS.array.maxLength}
                disabled={personality.length >= LIMITS.array.maxItems}
                placeholder={personality.length >= LIMITS.array.maxItems
                  ? t('settings.maxItemsReached', '上限に達しました')
                  : t('settings.personalityPlaceholder', '性格を追加...')}
                className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => addItem(newPersonality, setPersonality, setNewPersonality, LIMITS.array.maxItems)}
                disabled={personality.length >= LIMITS.array.maxItems}
                className="px-3 py-2 bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Likes */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('settings.likes', '好きなもの')}
              </label>
              <span className={`text-xs ${likes.length >= LIMITS.array.maxItems ? 'text-red-500' : 'text-gray-400'}`}>
                {likes.length}/{LIMITS.array.maxItems}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              {likes.map((item, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-sm rounded-full"
                >
                  {item}
                  <button
                    type="button"
                    onClick={() => removeItem(index, setLikes)}
                    className="hover:text-gray-900 dark:hover:text-gray-100"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newLike}
                onChange={(e) => setNewLike(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, newLike, setLikes, setNewLike, LIMITS.array.maxItems)}
                maxLength={LIMITS.array.maxLength}
                disabled={likes.length >= LIMITS.array.maxItems}
                placeholder={likes.length >= LIMITS.array.maxItems
                  ? t('settings.maxItemsReached', '上限に達しました')
                  : t('settings.likesPlaceholder', '好きなものを追加...')}
                className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => addItem(newLike, setLikes, setNewLike, LIMITS.array.maxItems)}
                disabled={likes.length >= LIMITS.array.maxItems}
                className="px-3 py-2 bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Dislikes */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('settings.dislikes', '嫌いなもの')}
              </label>
              <span className={`text-xs ${dislikes.length >= LIMITS.array.maxItems ? 'text-red-500' : 'text-gray-400'}`}>
                {dislikes.length}/{LIMITS.array.maxItems}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              {dislikes.map((item, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-sm rounded-full"
                >
                  {item}
                  <button
                    type="button"
                    onClick={() => removeItem(index, setDislikes)}
                    className="hover:text-gray-900 dark:hover:text-gray-100"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newDislike}
                onChange={(e) => setNewDislike(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, newDislike, setDislikes, setNewDislike, LIMITS.array.maxItems)}
                maxLength={LIMITS.array.maxLength}
                disabled={dislikes.length >= LIMITS.array.maxItems}
                placeholder={dislikes.length >= LIMITS.array.maxItems
                  ? t('settings.maxItemsReached', '上限に達しました')
                  : t('settings.dislikesPlaceholder', '嫌いなものを追加...')}
                className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => addItem(newDislike, setDislikes, setNewDislike, LIMITS.array.maxItems)}
                disabled={dislikes.length >= LIMITS.array.maxItems}
                className="px-3 py-2 bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-red-500 text-sm text-center">{error}</p>
          )}
        </form>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {t('common.cancel', 'キャンセル')}
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white font-medium rounded-lg transition-colors"
          >
            {isSubmitting ? t('common.saving', '保存中...') : t('common.save', '保存')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CharacterSettingsModal;
